import { supabase } from '../lib/supabase';

export type PackSubscription = {
  id: string;
  pack_id: string;
  status: string;
  cancel_at_period_end: boolean;
  current_period_end: string | null;
  next_billing_at: string | null;
  credits_per_period: number;
  cashfree_subscription_id: string;
  created_at: string;
};

export async function getActivePackSubscription(): Promise<PackSubscription | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { data } = await supabase
    .from('pack_subscriptions')
    .select('id, pack_id, status, cancel_at_period_end, current_period_end, next_billing_at, credits_per_period, cashfree_subscription_id, created_at')
    .eq('user_id', user.id)
    .in('status', ['active', 'bank_approval_pending'])
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  return data as PackSubscription | null;
}

export async function cancelPackSubscription(
  packSubscriptionId: string,
): Promise<{ success: boolean; error?: string; period_end?: string | null; message?: string }> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    return { success: false, error: 'Not signed in' };
  }

  const resp = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/cancel-cashfree-subscription`,
    {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ pack_subscription_id: packSubscriptionId }),
    },
  );

  const body = await resp.json().catch(() => ({}));
  if (!resp.ok) {
    return { success: false, error: body.error || 'Cancel failed' };
  }
  return {
    success: true,
    period_end: body.period_end,
    message: body.message,
  };
}

export async function recordSubscriptionPaymentFailed(
  subscriptionId: string,
  reason: string,
): Promise<void> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token || !subscriptionId) return;

  try {
    await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/verify-pack-subscription?subscription_id=${encodeURIComponent(subscriptionId)}&mark_failed=1`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ subscription_id: subscriptionId, mark_failed: true, reason }),
      },
    );
  } catch (err) {
    console.warn('[recordSubscriptionPaymentFailed]', err);
  }
}

export async function verifyPackSubscription(subscriptionId: string): Promise<{
  status: string;
  pack_id?: string;
  period_end?: string | null;
  auth_failed?: boolean;
  cashfree_status?: string | null;
  authorization_status?: string | null;
  credits_per_period?: number;
}> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    return { status: 'pending' };
  }

  const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/verify-pack-subscription?subscription_id=${encodeURIComponent(subscriptionId)}`;
  const resp = await fetch(url, {
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
    },
  });
  if (!resp.ok) return { status: 'pending' };
  return resp.json();
}
